import { useEffect, useState, useRef } from "react";
import useApi from "./useApi";

// Cache user data globally to prevent multiple API calls
let cachedUser = null;
let cachedToken = null;

const useUserMe = () => {
  const { request } = useApi();
  const [user, setUser] = useState(() => cachedUser);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("userId"));
  const requestRef = useRef(request);

  // Keep request ref updated
  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  useEffect(() => {
    const syncToken = () => {
      const newToken = localStorage.getItem("userId");
      // Clear cache when token changes (new login or logout)
      if (newToken !== token) {
        if (!newToken) {
          // Logout - clear everything
          cachedUser = null;
          cachedToken = null;
        } else if (cachedToken && cachedToken !== newToken) {
          // New login - clear old cache
          cachedUser = null;
          cachedToken = null;
        }
      }
      setToken(newToken);
    };
    const handleStorage = (event) => {
      if (!event || event.key === "userId") {
        syncToken();
      }
    };
    window.addEventListener("auth-change", syncToken);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("auth-change", syncToken);
      window.removeEventListener("storage", handleStorage);
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      cachedUser = null;
      cachedToken = null;
      setUser(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Clear cache if token changed (new login)
    if (cachedToken && cachedToken !== token) {
      cachedUser = null;
      cachedToken = null;
      setUser(null); // Clear user state when token changes
    }

    // If we already have cached user data for this token, use it immediately
    if (cachedUser && cachedToken === token) {
      setUser(cachedUser);
      setIsLoading(false);
      setError(null); // Clear any previous errors
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchUser = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await requestRef.current({
          path: "/user/me",
          signal: controller.signal,
        });
        
        // Cache the user data
        cachedUser = data || null;
        cachedToken = token;
        
        if (isMounted && cachedToken === token) {
          setUser(cachedUser);
          setError(null); // Clear any errors on success
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (isMounted) {
          // Check if error is from useApi redirect (401/403)
          // useApi already handles 401/403 and redirects, so we don't need to redirect again
          const errorMessage = err?.message?.toLowerCase() || "";
          const isAuthError = errorMessage.includes("401") || 
                             errorMessage.includes("403") || 
                             errorMessage.includes("unauthorized") || 
                             errorMessage.includes("forbidden") ||
                             errorMessage.includes("expired") ||
                             errorMessage.includes("session expired");
          
          // Only clear cache and user if it's an auth error
          // Note: useApi already redirected, so we just need to clean up state
          if (isAuthError) {
            cachedUser = null;
            cachedToken = null;
            setUser(null);
            // Don't set error here - useApi already handled the redirect
          } else {
            // For non-auth errors (network, etc.), set error but don't clear user
            // This allows the user to retry or see the error
            setError(err?.message || "Failed to load user.");
          }
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchUser();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [token]);

  return { user, isLoading, error };
};

export default useUserMe;
